import { useEffect, useState, useCallback } from "react";
import Box from "./box";
import Button from "./button";
import Chip from "./chip";
import Input from "./input";
import Textarea from "./textarea";
import Alert from "./alert";
import { Api } from "../services/Api";
import { Category } from "../types/Category";
import { Tag } from "../types/Tag";
import Select from "./select";
import { GenerationInterface } from "../layout/generate";
import { platformEvents } from "../services/eventbus/platformEvents";

interface Props {
  generation: GenerationInterface;
  onClose: () => void;
  onSave?: () => void;
}

export default function SaveGeneration({ generation, onClose, onSave }: Props) {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [title, setTitle] = useState("");
  const [titleLength, setTitleLength] = useState(0);
  const titleMaxLength = 200;
  const [description, setDescription] = useState("");
  const [descriptionLength, setDescriptionLength] = useState(0);
  const descriptionMaxLength = 600;
  const [tags, setTags] = useState<[number, string][]>([]);
  const [selectedTags, setSelectedTags] = useState<[number, string][]>([]);
  const [error] = useState("");
  const [categories, setCategories] = useState<[number, string][]>([]);
  const [selectedCategories, setSelectedCategories] = useState<
    [number, string][]
  >([]);
  const [pathStrings, setPathStrings] = useState<string[]>([]);
  const [tagStrings, setTagStrings] = useState<string[]>([]);

  const handleSave = () => {
    if (generation.id && title && description && selectedCategories.length > 0 && selectedTags.length > 0 && generation.image_url && generation.image_url.length > 0) {
      setLoading(true);
      Api(`/users/content/generate/${generation.id}/convert`, {
        method: "POST",
        data: {
          title,
          description,
          categories: selectedCategories.map(([id, _]) => id),
          tags: selectedTags.map(([id, _]) => id),
        },
      }).then(_ => {
        setLoading(false);
        setCurrentStep(1);
        setTitle("");
        setDescription("");
        setTags([]);
        platformEvents.emit("generalSuccess", "Generation saved successfully. You can now view it in your library.");
        if (onSave) {
          onSave();
        }
      }).catch((data) => {
        setLoading(false);
        if (data.message) {
          platformEvents.emit("generalFailure", data.message);
        } else if (data.error) {
          platformEvents.emit("generalFailure", data.error);
        }
      });
    }
  };

  const handleCancel = () => {
    setCurrentStep(1);
    setTitle("");
    setDescription("");
    setTags([]);
    setLoading(false);
    onClose();
  };

  const handleCategoryClick = (name: string) => {
    // Find the original tuple from categories based on name
    const category = categories.find(
      ([, categoryName]) => categoryName === name,
    );
    // Check if it is already selected and do nothing if it is
    if (category) {
      if (selectedCategories.includes(category)) {
        return;
      }
      // Add the selected category to the selectedCategories tuple array
      setSelectedCategories([...selectedCategories, category]);
    }
  };

  const handleRemoveCategory = (category: [number, string]) => {
    // Remove the tuple from the selectedCategories tuple array
    setSelectedCategories(selectedCategories.filter((c) => c !== category));
  };

  const handleTagClick = (name: string) => {
    const tag = tags.find(([, tagName]) => tagName === name);
    if (tag) {
      if (selectedTags.includes(tag)) {
        return;
      }
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleRemoveTag = (tag: [number, string]) => {
    setSelectedTags(selectedTags.filter((c) => c !== tag));
  };

  const flattenCategories = useCallback(
    (categories: Category[], path: string = ""): [number, string][] => {
      const result: [number, string][] = [];

      categories.forEach((category) => {
        const currentPath =
          path === "" ? category.name : `${path} > ${category.name}`;
        result.push([category.id, currentPath]);
        result.push(...flattenCategories(category.all_children, currentPath));
      });

      return result;
    },
    [],
  );

  useEffect(() => {
    Api("/browse/categories").then((data) => {
      setCategories(flattenCategories(data));
    });
    Api("/browse/tags").then((data: Array<Tag>) => {
      setTags(data.map((tag) => [tag.id, tag.tag]));
    });
  }, [flattenCategories]);

  useEffect(() => {
    setPathStrings(categories.map(([, path]) => path));
    setTagStrings(tags.map(([, tag]) => tag));
  }, [categories, tags]);

  if (currentStep === 1) {
    return (
      <Box size="xl">
        <div className="space-y-lg">
          <h2 className="text-center text-xl">Add details</h2>

          <div className="space-y-lg">
            {error && <Alert colour="danger" text={error} />}

            <div className="space-y-sm">
              <div className="flex justify-between">
                <div className="flex items-center gap-sm">
                  <span className="h-xs w-xs rounded-full bg-purple-100"></span>
                  <label htmlFor="title">Title (required)</label>
                </div>
                <span className="text-slate-100">
                  {titleLength}/{titleMaxLength}
                </span>
              </div>
              <Input
                type="text"
                id="title"
                placeholder="Title"
                onChange={(e) => {
                  setTitle(e.target.value);
                  setTitleLength(e.target.value.length);
                }}
                value={title}
                maxLength={titleMaxLength}
              />
            </div>

            <div className="space-y-sm">
              <div className="flex justify-between">
                <div className="flex items-center gap-sm">
                  <span className="h-xs w-xs rounded-full bg-purple-100"></span>
                  <label htmlFor="description">Description (required)</label>
                </div>
                <span className="text-slate-100">
                  {descriptionLength}/{descriptionMaxLength}
                </span>
              </div>
              <Textarea
                id="description"
                placeholder="Description"
                rows={5}
                minLength={1}
                maxLength={descriptionMaxLength}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setDescriptionLength(e.target.value.length);
                }}
              />
            </div>

            <div className="space-y-md">
              <div className="space-y-sm">
                <div className="flex items-center gap-sm">
                  <span className="h-xs w-xs rounded-full bg-purple-100"></span>
                  <label htmlFor="categories">Categories (required)</label>
                </div>
                <div className="relative">
                  <Select
                    block
                    options={pathStrings}
                    value="Select categories"
                    valueChanged={handleCategoryClick}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-sm">
                {selectedCategories.map((category) => (
                  <Chip
                    key={"category-" + category}
                    colour="secondary"
                    text={category[1]}
                    onClick={() => handleRemoveCategory(category)}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-md">
              <div className="space-y-sm">
                <div className="flex items-center gap-sm">
                  <span className="h-xs w-xs rounded-full bg-purple-100"></span>
                  <label htmlFor="tags">Tags (required)</label>
                </div>
                <div className="relative">
                  <Select
                    block
                    options={tagStrings}
                    value="Select tags"
                    valueChanged={handleTagClick}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-sm">
                {selectedTags.map((tag) => (
                  <Chip
                    key={"tag-" + tag}
                    colour="secondary"
                    text={tag[1]}
                    onClick={() => handleRemoveTag(tag)}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-between border-t border-slate-200 pt-lg">
              <Button onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                primary
                disabled={
                  !generation ||
                  !generation.image_url ||
                  !title ||
                  !description ||
                  selectedCategories.length === 0 ||
                  selectedTags.length === 0 ||
                  loading
                }
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </Box>
    );
  }
}
